import { request, EMISSION_BASE, ORGANISATION_BASE } from "@/api/client";
import { fetchBusinessUnits } from "@/features/facilities/api/businessUnitService";
import { getAvailableCategories as fetchAvailableCategories } from "@/features/emissions/api/emissionService";
import type { BusinessUnitOption } from "@/features/facilities/api/businessUnitService";
import type {
  EmissionsByScopeAndMonthResponse,
  EmissionsSankeyItem,
  FacilityEmission,
  ActivityEmission,
  EmissionsByScopeAndCategoryResponse,
  YearEmission,
  TargetYearValue,
  MetricYearValue,
  TargetActualResponse,
  OffsetsByMonthResponse,
  CategoryOption,
} from "../types/dashboard";

import { toLocalIsoDate } from "@/lib/date";
import { normalizeMonthlyEmissions } from "../lib/monthlyData";

interface EmissionParams {
  businessUnitIds: string;
  from_date: Date;
  to_date: Date;
  categories: string;
  scopes?: string;
}

function toParams(base: EmissionParams): Record<string, string> {
  return {
    ...(base.businessUnitIds ? { businessUnitIds: base.businessUnitIds } : {}),
    from_date: toLocalIsoDate(base.from_date),
    to_date: toLocalIsoDate(base.to_date),
    categories: base.categories,
    ...(base.scopes ? { scopes: base.scopes } : {}),
  };
}

function toMonthParam(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export function getEmissionsByScopeAndMonth(
  params: EmissionParams,
): Promise<EmissionsByScopeAndMonthResponse> {
  return request("get", `${EMISSION_BASE}/emissionsbyscopeandmonth`, {
    params: toParams(params),
    unwrap: false,
  });
}

export function getAllEmissions(
  params: EmissionParams,
): Promise<EmissionsSankeyItem[]> {
  return request("get", `${EMISSION_BASE}/allemissions`, {
    params: toParams(params),
  });
}

export async function getEmissionsByMonth(
  params: EmissionParams,
): Promise<EmissionsByScopeAndMonthResponse> {
  const raw = await request("get", `${EMISSION_BASE}/allemissionsbymonth`, {
    params: toParams(params),
    unwrap: false,
  });
  return normalizeMonthlyEmissions(raw);
}

export function getEmissionsByUnit(
  params: EmissionParams,
): Promise<FacilityEmission[]> {
  return request("get", `${EMISSION_BASE}/emissionsbyunit`, {
    params: toParams(params),
  });
}

export function getEmissionsByActivity(
  params: EmissionParams,
): Promise<ActivityEmission[]> {
  return request("get", `${EMISSION_BASE}/emissionsbyactivity`, {
    params: toParams(params),
  });
}

export function getEmissionsByScopeAndCategories(
  params: EmissionParams,
): Promise<EmissionsByScopeAndCategoryResponse> {
  return request("get", `${EMISSION_BASE}/emissionsbyscopeandcategories`, {
    params: toParams(params),
    unwrap: false,
  });
}

export function getFilteredEmissionsByYear(
  params: EmissionParams,
): Promise<YearEmission[]> {
  return request("get", `${EMISSION_BASE}/emissionsbyyear`, {
    params: toParams(params),
  });
}

export function getEmissionsByYear(
  businessUnit: string,
  scope: string,
  category: string,
): Promise<YearEmission[]> {
  return request("get", `${EMISSION_BASE}/emissionsbyyear`, {
    params: {
      businessUnit,
      ...(scope ? { scope } : {}),
      ...(category ? { category } : {}),
    },
  });
}

export function getTargetActualEmissions(
  businessUnit: string,
  years: string,
): Promise<TargetActualResponse> {
  return request("get", `${EMISSION_BASE}/target-actual-emissions`, {
    params: {
      businessUnit,
      ...(years ? { years } : {}),
    },
  });
}

export async function getTargetCategories(businessUnit: string) {
  const data = await request<{
    categories: Array<{ category: number; code?: string; name?: string }>;
  }>("get", `${ORGANISATION_BASE}/scope-category-targets-year`, {
    params: { businessUnit },
  });
  return data.categories;
}

export function getTargetsByYear(
  businessUnit: string,
  type: string,
  scope: string,
  category: string,
): Promise<TargetYearValue[]> {
  return request("get", `${ORGANISATION_BASE}/targets-year`, {
    params: {
      businessUnit,
      ...(type ? { type } : {}),
      ...(scope ? { scope } : {}),
      ...(category ? { category } : {}),
    },
  });
}

export function getMetricsByYear(
  businessUnit: string,
  metric: string,
  type: string,
  scope: string,
  category: string,
): Promise<MetricYearValue[]> {
  return request("get", `${ORGANISATION_BASE}/metrics-year`, {
    params: {
      businessUnit,
      ...(metric ? { metric } : {}),
      ...(type ? { type } : {}),
      ...(scope ? { scope } : {}),
      ...(category ? { category } : {}),
    },
  });
}

export function getOffsetsByMonth(params: {
  businessUnits: string;
  fromMonth: Date;
  toMonth: Date;
}): Promise<OffsetsByMonthResponse> {
  return request("get", `${ORGANISATION_BASE}/offsets-month`, {
    params: {
      businessUnits: params.businessUnits,
      fromMonth: toMonthParam(params.fromMonth),
      toMonth: toMonthParam(params.toMonth),
    },
  });
}

export function getAvailableCategories(
  company: string,
): Promise<CategoryOption[]> {
  return fetchAvailableCategories(company);
}

export async function getFacilities(): Promise<{
  facilities: BusinessUnitOption[];
  totalCount: number;
}> {
  const { businessUnits, totalCount } = await fetchBusinessUnits();
  return { facilities: businessUnits, totalCount };
}

export function getOffsetsByYear(
  businessUnits: string,
): Promise<Array<{ year: number; value: number }>> {
  return request("get", `${ORGANISATION_BASE}/offsets-year`, {
    params: { businessUnits },
  });
}

export function getCustomMetrics(
  businessUnit: string,
): Promise<Array<{ type: string }>> {
  return request("get", `${ORGANISATION_BASE}/custom-metrics`, {
    params: { businessUnit },
  });
}
